/**
 * Custom hook for befaring validering
 * Sjekker hva som MÅ gjøres på stedet vs. hva som kan gjøres på kontoret
 */
import { useMemo } from 'react';

export function useBefaringValidation(befaringData) {
  return useMemo(() => {
    const kritiskeFeil = [];
    const advarsler = [];

    if (!befaringData || !befaringData.bygningsdeler) {
      return {
        canLeave: false,
        canGenerateReport: false,
        kritiskeFeil: [],
        advarsler: [],
        total: { kritisk: 0, advarsel: 0 }
      };
    }

    // Sjekk alle bygningsdeler
    befaringData.bygningsdeler.forEach(del => {
      // KRITISK 1: Påkrevde bygningsdeler ikke inspisert
      if (del.pakrevd && (!del.svar || del.svar.length === 0)) {
        kritiskeFeil.push({
          type: 'REQUIRED_NOT_INSPECTED',
          bygningsdel: del.bygningsdel_type,
          bygningsdel_id: del.id,
          message: `${del.bygningsdel_type} er påkrevd i NS3600 og må inspiseres`,
          action: 'navigate',
          severity: 'critical'
        });
      }

      // Sjekk alle svar i bygningsdelen
      if (del.svar && del.svar.length > 0) {
        del.svar.forEach(svar => {
          const svarVerdi = svar.svar_verdi || '';

          // KRITISK 2: TGIU uten begrunnelse
          if (svarVerdi.includes('TGIU') && !svar.tgiu_begrunnelse) {
            kritiskeFeil.push({
              type: 'TGIU_NO_REASON',
              bygningsdel: del.bygningsdel_type,
              bygningsdel_id: del.id,
              sporsmal_id: svar.sporsmal_id,
              sporsmal_tekst: svar.sporsmal_tekst,
              message: `TGIU må ha begrunnelse (§2-22): "${svar.sporsmal_tekst}"`,
              action: 'edit_tgiu',
              severity: 'critical'
            });
          }

          // KRITISK 3: TG2/TG3 uten bilder
          const isTG2orTG3 = svarVerdi.includes('TG2') || svarVerdi.includes('TG3');
          if (isTG2orTG3) {
            const bilderCount = (del.bilder || []).length;
            if (bilderCount < 2) {
              kritiskeFeil.push({
                type: 'CRITICAL_NO_IMAGES',
                bygningsdel: del.bygningsdel_type,
                bygningsdel_id: del.id,
                svar_verdi: svarVerdi,
                message: `${svarVerdi} krever minimum 2 bilder (kun ${bilderCount} tatt)`,
                action: 'take_photo',
                severity: 'critical'
              });
            }
          }

          // ADVARSEL 1: TG3 uten kostnadsestimat (OK - gjøres på kontoret)
          if (svarVerdi.includes('TG3') && !svar.kostnadsestimat) {
            advarsler.push({
              type: 'TG3_NO_COST',
              bygningsdel: del.bygningsdel_type,
              bygningsdel_id: del.id,
              sporsmal_id: svar.sporsmal_id,
              message: `TG3: Kostnadsestimat mangler (kan fylles inn på kontoret)`,
              action: 'info',
              severity: 'warning'
            });
          }

          // ADVARSEL 2: TG2 uten redegjøring (OK - gjøres på kontoret)
          if (svarVerdi.includes('TG2') && !svar.redegjoring) {
            advarsler.push({
              type: 'TG2_NO_EXPLANATION',
              bygningsdel: del.bygningsdel_type,
              bygningsdel_id: del.id,
              sporsmal_id: svar.sporsmal_id,
              message: `TG2: Redegjøring mangler (kan fylles inn på kontoret)`,
              action: 'info',
              severity: 'warning'
            });
          }
        });
      }

      // ADVARSEL 3: For få bilder (anbefaling: minimum 3 per bygningsdel)
      const bilderCount = (del.bilder || []).length;
      if (bilderCount > 0 && bilderCount < 3) {
        advarsler.push({
          type: 'FEW_IMAGES',
          bygningsdel: del.bygningsdel_type,
          bygningsdel_id: del.id,
          message: `${del.bygningsdel_type}: Kun ${bilderCount} bilder (anbefalt: 3+)`,
          action: 'take_photo',
          severity: 'info'
        });
      }
    });

    // Sjekk videre undersøkelser (hvis auto-generert, må være bekreftet)
    if (befaringData.videre_undersokelser && befaringData.videre_undersokelser.length > 0) {
      befaringData.videre_undersokelser.forEach(vu => {
        if (vu.auto_generated && !vu.bekreftet) {
          advarsler.push({
            type: 'VIDERE_UNDERSOKELSE_NOT_CONFIRMED',
            kategori: vu.undersokelse_kategori,
            message: `Videre undersøkelse (${vu.undersokelse_kategori}) må bekreftes`,
            action: 'confirm',
            severity: 'warning'
          });
        }
      });
    }

    return {
      canLeave: kritiskeFeil.length === 0,
      canGenerateReport: kritiskeFeil.length === 0,
      kritiskeFeil,
      advarsler,
      total: {
        kritisk: kritiskeFeil.length,
        advarsel: advarsler.length
      },
      message: kritiskeFeil.length === 0
        ? '✅ JA, DU KAN REISE'
        : `🚫 NEI - ${kritiskeFeil.length} kritiske mangler må fikses først`
    };
  }, [befaringData]);
}

export default useBefaringValidation;
